import React from 'react';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import SettingsIcon from '@atlaskit/icon/glyph/settings';
import { SITE_ADMIN_SETTINGS_ITEM_HREFS } from '@atlassian/jira-atlassian-navigation-global-settings-home';
import {
	useTransformNavigationCommands,
	COMMAND_PALETTE_SECTIONS,
	useCommandKeywords,
	COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY,
} from '@atlassian/jira-command-palette-common';

import {
	COMMAND_PALETTE_ATLASSIAN_ADMIN_PRIORITY,
	COMMAND_PALETTE_PERSONAL_SETTINGS_PRIORITY,
	COMMAND_PALETTE_PROJECT_NAVIGATION_PRIORITY,
} from '@atlassian/jira-command-palette-common/src/common/constants';
import { useIntl } from '@atlassian/jira-intl';
import { useAtlassianPersonalSettingsLink } from '@atlassian/jira-navigation-apps-common';
import { getGeneralPersonalSettingsPath } from '@atlassian/jira-personal-settings-paths';
import { useNavigationPlansVisibility } from '@atlassian/jira-plans-item-visibility';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current';
import {
	boardsDirectoryRoute,
	dashboardsDirectoryRoute,
	filtersDirectoryRoute,
	plansDirectoryRoute,
	projectsDirectoryRoute,
} from '@atlassian/jira-router-routes-directories-routes';
import { getGeneralPersonalSettingsRouteForLink } from '@atlassian/jira-router-routes-personal-settings-routes';
import type { User } from '@atlassian/jira-shared-types';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { useIssueNavigatorLink } from '../../../controllers/use-issue-navigator-link';
import {
	GO_TO_ISSUES_COMMAND_ID,
	GO_TO_PERSONAL_SETTINGS_COMMAND_ID,
	VIEW_ALL_BOARDS_COMMAND_ID,
	VIEW_ALL_DASHBOARDS_COMMAND_ID,
	VIEW_ALL_FILTERS_COMMAND_ID,
	VIEW_ALL_PLANS_COMMAND_ID,
	VIEW_ALL_PROJECTS_COMMAND_ID,
	GO_TO_ACCOUNT_SETTINGS_COMMAND_ID,
	SITE_ADMIN_USER_MANAGEMENT,
	SITE_ADMIN_BILLING,
} from './constants';
import messages from './messages';

export const useNavigationCommands = () => {
	const { formatMessage } = useIntl();
	const { getKeywords } = useCommandKeywords();
	const { issueNavigatorLink } = useIssueNavigatorLink();

	const { hideAndTransformNavigationCommands } = useTransformNavigationCommands();

	const { data } = useCurrentUser();
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const email = (data.user as User)?.emailAddress;
	const accountSettingsLink = useAtlassianPersonalSettingsLink(email || undefined);

	const [isPlansVisible] = useNavigationPlansVisibility();

	const { isSiteAdmin } = useTenantContext();

	const commands = hideAndTransformNavigationCommands([
		{
			command: {
				id: VIEW_ALL_PROJECTS_COMMAND_ID,
				name: formatMessage(messages.viewAllProjects),
				keywords: getKeywords('viewAllProjectsSynonyms'),
				section: COMMAND_PALETTE_SECTIONS.siteNavigation,
				priority: COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY.goToProjects,
				components: {
					LeftIcon: () => <ArrowRightIcon label={formatMessage(messages.viewAllProjects)} />,
				},
				analytics: {
					action: 'viewAllProjects',
				},
			},
			path: projectsDirectoryRoute,
		},
		{
			command: {
				id: VIEW_ALL_FILTERS_COMMAND_ID,
				name: formatMessage(messages.viewAllFilters),
				keywords: getKeywords('viewAllFiltersSynonyms'),
				section: COMMAND_PALETTE_SECTIONS.siteNavigation,
				priority: COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY.goToFilters,
				components: {
					LeftIcon: () => <ArrowRightIcon label={formatMessage(messages.viewAllFilters)} />,
				},
				analytics: {
					action: 'viewAllFilters',
				},
			},
			path: filtersDirectoryRoute,
		},
		{
			command: {
				id: VIEW_ALL_DASHBOARDS_COMMAND_ID,
				name: formatMessage(messages.viewAllDashboards),
				keywords: getKeywords('viewAllDashboardsSynonyms'),
				section: COMMAND_PALETTE_SECTIONS.siteNavigation,
				priority: COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY.goToDashboards,
				components: {
					LeftIcon: () => <ArrowRightIcon label={formatMessage(messages.viewAllDashboards)} />,
				},
				analytics: {
					action: 'viewAllDashboards',
				},
			},
			path: dashboardsDirectoryRoute,
		},
		{
			command: {
				id: GO_TO_PERSONAL_SETTINGS_COMMAND_ID,
				name: formatMessage(messages.goToPersonalSettings),
				keywords: getKeywords('goToPersonalSettingsSynonyms'),
				section: COMMAND_PALETTE_SECTIONS.personalSettings,
				priority: COMMAND_PALETTE_PERSONAL_SETTINGS_PRIORITY.goToPersonalSettings,
				components: {
					LeftIcon: () => <SettingsIcon label={formatMessage(messages.goToPersonalSettings)} />,
				},
				analytics: {
					action: 'goToPersonalSettings',
				},
			},
			path: getGeneralPersonalSettingsRouteForLink(),
			hideCommandPath: getGeneralPersonalSettingsPath(),
		},
		{
			command: {
				id: VIEW_ALL_BOARDS_COMMAND_ID,
				name: formatMessage(messages.viewAllBoards),
				keywords: getKeywords('viewAllBoardsSynonyms'),
				section: COMMAND_PALETTE_SECTIONS.siteNavigation,
				components: {
					LeftIcon: () => <ArrowRightIcon label={formatMessage(messages.viewAllBoards)} />,
				},
				priority: COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY.goToBoards,
				analytics: {
					action: 'viewAllBoards',
				},
			},
			path: boardsDirectoryRoute,
		},
		{
			command: {
				id: GO_TO_ACCOUNT_SETTINGS_COMMAND_ID,
				name: formatMessage(messages.goToAccountSettings),
				keywords: getKeywords('goToAccountSettingsSynonyms'),
				section: COMMAND_PALETTE_SECTIONS.personalSettings,
				components: {
					LeftIcon: () => <SettingsIcon label={formatMessage(messages.goToAccountSettings)} />,
				},
				priority: COMMAND_PALETTE_PERSONAL_SETTINGS_PRIORITY.goToAccountSettings,
				analytics: {
					action: 'goToAccountSettings',
				},
			},
			path: accountSettingsLink,
			shouldAlwaysOpenNewTab: true,
		},
		...(issueNavigatorLink
			? [
					{
						command: {
							id: GO_TO_ISSUES_COMMAND_ID,
							name: formatMessage(messages.goToIssues),
							keywords: getKeywords('goToIssuesSynonyms'),
							section: COMMAND_PALETTE_SECTIONS.projectNavigation,
							components: {
								LeftIcon: () => <ArrowRightIcon label={formatMessage(messages.goToIssues)} />,
							},
							priority: COMMAND_PALETTE_PROJECT_NAVIGATION_PRIORITY.goToIssues,

							analytics: {
								action: 'goToIssues',
							},
						},
						path: issueNavigatorLink,
						hideCommandPath: projectsDirectoryRoute.path,
					},
				]
			: []),
		...(isPlansVisible
			? [
					{
						command: {
							id: VIEW_ALL_PLANS_COMMAND_ID,
							name: formatMessage(messages.viewAllPlans),
							keywords: getKeywords('viewAllPlansSynonyms'),
							section: COMMAND_PALETTE_SECTIONS.siteNavigation,
							components: {
								LeftIcon: () => <ArrowRightIcon label={formatMessage(messages.viewAllPlans)} />,
							},
							priority: COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY.goToPlans,
							analytics: {
								action: 'viewAllPlans',
							},
						},
						path: plansDirectoryRoute,
					},
				]
			: []),
		...(isSiteAdmin
			? [
					{
						command: {
							id: SITE_ADMIN_USER_MANAGEMENT,
							name: formatMessage(messages.goToUserManagement),
							keywords: getKeywords('goToUserManagementSynonyms'),
							section: COMMAND_PALETTE_SECTIONS.atlassianAdmin,
							priority: COMMAND_PALETTE_ATLASSIAN_ADMIN_PRIORITY.userManagement,
							components: {
								LeftIcon: () => <SettingsIcon label={formatMessage(messages.goToUserManagement)} />,
							},
							analytics: {
								action: 'goToUserManagement',
							},
						},
						path: SITE_ADMIN_SETTINGS_ITEM_HREFS.USER_MANAGEMENT,
						shouldAlwaysOpenNewTab: true,
					},
					{
						command: {
							id: SITE_ADMIN_BILLING,
							name: formatMessage(messages.goToBilling),
							section: COMMAND_PALETTE_SECTIONS.atlassianAdmin,
							priority: COMMAND_PALETTE_ATLASSIAN_ADMIN_PRIORITY.billing,
							components: {
								LeftIcon: () => <SettingsIcon label={formatMessage(messages.goToBilling)} />,
							},
							analytics: {
								action: 'goToBilling',
							},
						},
						path: SITE_ADMIN_SETTINGS_ITEM_HREFS.BILLING,
						shouldAlwaysOpenNewTab: true,
					},
				]
			: []),
	]);

	return commands;
};
